import { useEffect, useState } from 'react';
import { listMonitors, type Category, type Meta, type Monitor, type MonitorListParams, type Resolution } from '@/lib/api';
import { PAGE_LIMIT } from '@/lib/constants';
import { useDebounced } from './use-debounced';
import type { FilterState } from '@/components/filter-bar';

export interface MonitorListState {
  data: Monitor[];
  meta: Meta;
  loading: boolean;
  error: string | null;
}

const DEFAULT_META: Meta = { total: 0, page: 1, limit: PAGE_LIMIT, pages: 1 };

export function useMonitorList(filter: FilterState, page: number, limit = PAGE_LIMIT): MonitorListState {
  const debouncedSearch = useDebounced(filter.search, 300);
  const [state, setState] = useState<MonitorListState>({
    data: [],
    meta: DEFAULT_META,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const params: MonitorListParams = {
      page,
      limit,
      sort: filter.sort,
      brand: filter.brand || undefined,
      resolution: (filter.resolution || undefined) as Resolution | undefined,
      size: filter.size ? Number(filter.size) : undefined,
      search: debouncedSearch || undefined,
    };
    if (filter.category === 'focal') params.is_focal = '1';
    else if (filter.category) params.category = filter.category as Category;

    listMonitors(params)
      .then(({ data, meta }) => {
        if (cancelled) return;
        setState({ data, meta, loading: false, error: null });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setState({ data: [], meta: DEFAULT_META, loading: false, error: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [filter.brand, filter.category, filter.resolution, filter.size, filter.sort, debouncedSearch, page, limit]);

  return state;
}
